'use client';

import { useId, type ReactNode } from 'react';
import { UiIcon } from '@/components/ui/UiIcon';
import { cn } from '@/lib/utils/tw-merge';
import { useFooterAccordion } from './FooterAccordion';

type FooterSectionProps = {
    title: string;
    children: ReactNode;
    className?: string;
    panelClassName?: string;
};

/**
 * One footer column. Below lg it is a collapsible row, one open at a time
 * (FooterAccordion); from lg the same markup is a grid column whose toggle the
 * stylesheet makes inert. Collapsing runs through `data-open` and CSS rather
 * than the `hidden` attribute, so the panel is back in the accessibility tree
 * wherever it is visible.
 */
export function FooterSection({ title, children, className, panelClassName }: FooterSectionProps) {
    const panelId = useId();
    const { isOpen, toggle } = useFooterAccordion(panelId);

    return (
        <div className={cn('site-footer__section', className)} data-open={isOpen}>
            <button
                type="button"
                className="site-footer__toggle"
                onClick={toggle}
                aria-expanded={isOpen}
                aria-controls={panelId}
            >
                <span className="site-footer__head">{title}</span>
                <UiIcon name="chevron-down" className="site-footer__chevron" aria-hidden="true" />
            </button>

            <div className="site-footer__collapse">
                <div id={panelId} className={cn('site-footer__panel', panelClassName)}>
                    {children}
                </div>
            </div>
        </div>
    );
}
